import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client.ts';
import { DEPARTMENTS } from '@cg-idea/common';
import { Edit2, Check, X, Plus, Search, ChevronDown, ChevronRight, User as UserIcon, Trash2 } from 'lucide-react';
import { Layout } from '../components/Layout';

interface UserMapping {
  id: string;
  name: string;
  email: string;
  role: 'LEADER' | 'COMMITTEE_HEAD' | 'COMMITTEE_MEMBER' | 'EXTERNAL_COMMITTEE' | 'EXECUTION_COMMITTEE';
  managedDepartments: string[];
  reportsToId?: string | null;
}

interface Employee {
  id: string;
  name: string;
  email: string;
  department: string | null;
}

export const AdminDepartmentMapping = () => {
  const queryClient = useQueryClient();
  const [editingUserEmail, setEditingUserEmail] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<UserMapping>>({});
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  const [deptSearch, setDeptSearch] = useState('');
  const [isEmpDropdownOpen, setIsEmpDropdownOpen] = useState(false);

  // Track expanded tree nodes
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});

  // Fetch users with roles
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['userRoleMappings'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/user-role-mappings');
      return res.data.data as UserMapping[];
    },
  });

  // Fetch employee directory for searchable dropdown
  const { data: employeesData } = useQuery({
    queryKey: ['employees'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/employees');
      return res.data.data as Employee[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: { name: string; email: string; role: string; departments: string[]; reportsToId?: string }) => {
      const res = await apiClient.post(`/admin/user-role-mappings`, payload);
      return res.data;
    },
    onSuccess: () => {
      window.alert('User role mapping updated successfully');
      queryClient.invalidateQueries({ queryKey: ['userRoleMappings'] });
      setEditingUserEmail(null);
      setIsAddingNew(false);
    },
    onError: (err: any) => {
      window.alert(err.response?.data?.error?.message || 'Failed to update user role mapping');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/admin/user-role-mappings/${id}`);
      return res.data;
    },
    onSuccess: () => {
      window.alert('User role mapping deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['userRoleMappings'] });
    },
    onError: (err: any) => {
      window.alert(err.response?.data?.error?.message || 'Failed to delete user role mapping');
    },
  });

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this mapping?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = (user: UserMapping) => {
    setEditingUserEmail(user.email);
    setEditForm({ ...user });
    setDeptSearch('');
    // Open the modal for editing by setting a state or just relying on editingUserEmail
    // Wait, the modal is currently keyed off `isAddingNew`. Let's just use `editingUserEmail !== null` for the modal.
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditingUserEmail('NEW');
    setEditForm({ name: '', email: '', role: 'COMMITTEE_MEMBER', managedDepartments: [], reportsToId: null });
    setEmpSearch('');
    setDeptSearch('');
  };

  const handleCancel = () => {
    setEditingUserEmail(null);
    setIsAddingNew(false);
    setEmpSearch('');
    setDeptSearch('');
  };

  const handleSave = () => {
    if (!editForm.name || !editForm.email || !editForm.role) {
      window.alert('User and Role are required.');
      return;
    }
    // Validation for hierarchy
    if (editForm.role === 'COMMITTEE_HEAD' && !editForm.reportsToId) {
      window.alert('Committee Head must report to a Leader.');
      return;
    }
    if (editForm.role === 'COMMITTEE_MEMBER' && !editForm.reportsToId) {
      window.alert('Committee Member must report to a Committee Head.');
      return;
    }

    updateMutation.mutate({
      name: editForm.name,
      email: editForm.email,
      role: editForm.role,
      departments: editForm.managedDepartments || [],
      reportsToId: (editForm.role === 'LEADER' || editForm.role === 'EXTERNAL_COMMITTEE' || editForm.role === 'EXECUTION_COMMITTEE') ? undefined : editForm.reportsToId || undefined,
    });
  };

  const toggleDepartment = (dept: string) => {
    const current = editForm.managedDepartments || [];
    if (current.includes(dept)) {
      setEditForm({ ...editForm, managedDepartments: current.filter((d) => d !== dept) });
    } else {
      setEditForm({ ...editForm, managedDepartments: [...current, dept] });
    }
  };

  const toggleNode = (id: string) => {
    setExpandedNodes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredEmployees = useMemo(() => {
    if (!employeesData) return [];
    if (!empSearch) return employeesData.slice(0, 50); // Show max 50 initially
    return employeesData.filter(e =>
      (e.name || '').toLowerCase().includes((empSearch || '').toLowerCase()) ||
      (e.email || '').toLowerCase().includes((empSearch || '').toLowerCase()) ||
      (e.department || '').toLowerCase().includes((empSearch || '').toLowerCase())
    ).slice(0, 50);
  }, [employeesData, empSearch]);

  const filteredDepartments = useMemo(() => {
    let base: string[] = [...DEPARTMENTS];

    if (editForm.role === 'LEADER') {
      // Exclude departments already assigned to OTHER leaders
      const assignedToOtherLeaders = (usersData || [])
        .filter(u => u.role === 'LEADER' && u.email !== editForm.email)
        .flatMap(u => u.managedDepartments || []);

      base = base.filter(d => !assignedToOtherLeaders.includes(d));
    } else if (editForm.role === 'COMMITTEE_HEAD' && editForm.reportsToId) {
      base = usersData?.find(u => u.id === editForm.reportsToId)?.managedDepartments || [];
    } else if (editForm.role === 'COMMITTEE_MEMBER' && editForm.reportsToId) {
      base = usersData?.find(u => u.id === editForm.reportsToId)?.managedDepartments || [];
    } else if (editForm.role === 'EXTERNAL_COMMITTEE' || editForm.role === 'EXECUTION_COMMITTEE') {
      base = [...DEPARTMENTS]; // Can be assigned any department
    } else {
      base = []; // Force selecting parent first
    }

    if (!deptSearch) return base;
    return base.filter(d => d.toLowerCase().includes(deptSearch.toLowerCase()));
  }, [deptSearch, editForm.role, editForm.reportsToId, usersData]);

  // Derived lists for dropdowns
  const leadersList = useMemo(() => (usersData || []).filter(u => u.role === 'LEADER'), [usersData]);
  const headsList = useMemo(() => (usersData || []).filter(u => u.role === 'COMMITTEE_HEAD'), [usersData]);

  // Build Tree Structure for Rendering
  const treeRows = useMemo(() => {
    if (!usersData) return [];
    const rows: { user: UserMapping, level: number }[] = [];

    // Auto-expand everything on initial load or just leave it collapsed? Let's leave leaders collapsed by default, or auto-expand if small?
    // We'll just rely on state. 

    const leaders = usersData.filter(u => u.role === 'LEADER');
    const heads = usersData.filter(u => u.role === 'COMMITTEE_HEAD');
    const members = usersData.filter(u => u.role === 'COMMITTEE_MEMBER');

    leaders.forEach(leader => {
      rows.push({ user: leader, level: 0 });
      if (expandedNodes[leader.id]) {
        const leaderHeads = heads.filter(h => h.reportsToId === leader.id);
        leaderHeads.forEach(head => {
          rows.push({ user: head, level: 1 });
          if (expandedNodes[head.id]) {
            const headMembers = members.filter(m => m.reportsToId === head.id);
            headMembers.forEach(member => {
              rows.push({ user: member, level: 2 });
            });
          }
        });
      }
    });

    // Handle orphans (users with no valid reportsToId or missing parents, or external/execution committees)
    const processedIds = new Set(rows.map(r => r.user.id));
    const orphans = usersData.filter(u => !processedIds.has(u.id));
    orphans.forEach(o => {
      rows.push({ user: o, level: 0 }); // Show at root level if orphaned or independent
    });

    return rows;
  }, [usersData, expandedNodes]);

  // The `renderEditableRow` function has been removed as we are moving editing to the modal.


  return (
    <Layout title="Committee Hierarchy Mapping">
      <div className="flex-1 overflow-y-auto p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-500 mt-1">Map Leaders, Heads, and Members with direct reporting chains.</p>
            </div>
            <button
              onClick={handleAddNew}
              className="flex items-center gap-2 px-4 py-2 bg-[#1C4ED8] hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" /> Add Role Mapping
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-1/4">User Hierarchy</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-1/6">Role</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-1/4">Reporting To</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-1/4">Departments Assigned</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {isLoading ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-slate-500">Loading mappings...</td>
                    </tr>
                  ) : (
                    <>
                      {treeRows.map(({ user, level }) => {
                        // Render normally even if editing, the modal handles the actual editing state.

                        const hasChildren =
                          (user.role === 'LEADER' && headsList.some(h => h.reportsToId === user.id)) ||
                          (user.role === 'COMMITTEE_HEAD' && (usersData || []).some(m => m.reportsToId === user.id));

                        const isExpanded = expandedNodes[user.id];

                        return (
                          <tr key={user.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-6 py-4 align-top">
                              <div className="flex items-center" style={{ paddingLeft: `${level * 24}px` }}>
                                {hasChildren ? (
                                  <button onClick={() => toggleNode(user.id)} className="mr-2 text-slate-400 hover:text-blue-600 transition-colors">
                                    <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                  </button>
                                ) : (
                                  <div className="w-6 mr-2" /> // Spacer for alignment
                                )}
                                <div className="flex items-center gap-2">
                                  <div className={`p-1.5 rounded-full ${level === 0 ? 'bg-blue-100 text-blue-600' : level === 1 ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>
                                    <UserIcon className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <div className="font-bold text-slate-900">{user.name}</div>
                                    {user.email && <div className="text-[11px] text-slate-500">{user.email}</div>}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 align-top">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold border ${
                                user.role === 'LEADER' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                user.role === 'COMMITTEE_HEAD' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                user.role === 'EXTERNAL_COMMITTEE' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                                user.role === 'EXECUTION_COMMITTEE' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                                'bg-purple-50 text-purple-700 border-purple-200'
                              }`}>
                                {user.role === 'LEADER' ? 'Leader' :
                                  user.role === 'COMMITTEE_HEAD' ? 'Committee Head' :
                                  user.role === 'EXTERNAL_COMMITTEE' ? 'External Committee' :
                                  user.role === 'EXECUTION_COMMITTEE' ? 'Execution Committee' :
                                  'Committee Member'}
                              </span>
                            </td>
                            <td className="px-6 py-4 align-top">
                              {user.reportsToId ? (
                                <div className="flex flex-col">
                                  <span className="text-xs text-slate-500 mb-0.5 font-medium">
                                    {user.role === 'COMMITTEE_HEAD' ? 'Leader' : 'Committee Head'}
                                  </span>
                                  <span className="text-sm text-slate-800 font-bold bg-slate-100 px-2.5 py-1 rounded-md inline-block w-max border border-slate-200">
                                    {usersData?.find(u => u.id === user.reportsToId)?.name || 'Unknown'}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-sm text-slate-400 italic font-medium">Self</span>
                              )}
                            </td>
                            <td className="px-6 py-4 align-top">
                              <div className="flex flex-wrap gap-1.5">
                                {user.managedDepartments.map((d) => (
                                  <span key={d} className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md border bg-slate-50 text-slate-700 border-slate-200">
                                    {d}
                                  </span>
                                ))}
                                {user.managedDepartments.length === 0 && (
                                  <span className="text-xs text-slate-400 italic">No departments</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right align-top">
                              <div className="flex justify-end gap-1">
                                <button
                                  onClick={() => handleEdit(user)}
                                  disabled={editingUserEmail !== null}
                                  className="text-blue-600 hover:bg-blue-50 p-2 rounded-md transition-colors disabled:opacity-50"
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(user.id)}
                                  disabled={deleteMutation.isPending || editingUserEmail !== null}
                                  className="text-red-500 hover:bg-red-50 p-2 rounded-md transition-colors disabled:opacity-50"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {(!usersData || usersData.length === 0) && !isAddingNew && (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-slate-500">No hierarchy mapped yet. Click "Add Role Mapping" to begin forming committees.</td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Role Mapping Modal */}
      {(isAddingNew || editingUserEmail !== null) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <h3 className="text-lg font-bold text-slate-800">{isAddingNew ? 'Add Role Mapping' : 'Edit Role Mapping'}</h3>
              <button onClick={handleCancel} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-6">

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {isAddingNew ? 'Select Employee' : 'Employee Details'}
                  </label>
                  {isAddingNew ? (
                    <div className="relative">
                      <div className="relative">
                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                        <input
                          type="text"
                          placeholder="Search by name, email, or department..."
                          className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          value={empSearch}
                          onChange={(e) => {
                            setEmpSearch(e.target.value);
                            setIsEmpDropdownOpen(true);
                          }}
                          onFocus={() => setIsEmpDropdownOpen(true)}
                          onBlur={() => setTimeout(() => setIsEmpDropdownOpen(false), 200)}
                        />
                      </div>
                      {isEmpDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg z-20">
                          {filteredEmployees.length > 0 ? filteredEmployees.map(emp => (
                            <button
                              key={emp.id}
                              onClick={() => {
                                setEditForm({ ...editForm, name: emp.name, email: emp.email });
                                setEmpSearch(`${emp.name} (${emp.email})`);
                                setIsEmpDropdownOpen(false);
                              }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0"
                            >
                              <div className="flex items-center justify-between">
                                <div className="font-medium text-slate-900">{emp.name}</div>
                                {emp.department && (
                                  <div className="text-[10px] font-bold px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded uppercase tracking-wider">
                                    {emp.department}
                                  </div>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 mt-0.5">{emp.email}</div>
                            </button>
                          )) : (
                            <div className="px-3 py-2 text-sm text-slate-500 text-center">No employees found</div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-slate-50 p-3 rounded-md border border-slate-200 flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-slate-900">{editForm.name}</div>
                        <div className="text-xs text-slate-500">{editForm.email}</div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Assign Role</label>
                    <div className="relative">
                      <select
                        className="w-full appearance-none text-sm rounded-md border border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 pl-3 pr-10 bg-white"
                        value={editForm.role || 'COMMITTEE_MEMBER'}
                        onChange={(e) => {
                          const newRole = e.target.value as any;
                          setEditForm({ ...editForm, role: newRole, reportsToId: null });
                        }}
                      >
                        <option value="LEADER">Leader</option>
                        <option value="COMMITTEE_HEAD">Committee Head</option>
                        <option value="COMMITTEE_MEMBER">Committee Member</option>
                        <option value="EXTERNAL_COMMITTEE">External Committee</option>
                        <option value="EXECUTION_COMMITTEE">Execution Committee</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-500">
                        <ChevronDown className="w-4 h-4" />
                      </div>
                    </div>
                  </div>

                  <div>
                    {editForm.role === 'COMMITTEE_HEAD' && (
                      <>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Reports To (Leader)</label>
                        <select
                          className="w-full text-sm rounded-md border border-slate-300 shadow-sm py-2.5 px-3 bg-white focus:border-blue-500 focus:ring-blue-500"
                          value={editForm.reportsToId || ''}
                          onChange={(e) => setEditForm({ ...editForm, reportsToId: e.target.value })}
                        >
                          <option value="">-- Select Leader --</option>
                          {leadersList.filter(l => l.email !== editForm.email).map(l => (
                            <option key={l.id} value={l.id}>{l.name}</option>
                          ))}
                        </select>
                      </>
                    )}
                    {editForm.role === 'COMMITTEE_MEMBER' && (
                      <>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Reports To (Head)</label>
                        <select
                          className="w-full text-sm rounded-md border border-slate-300 shadow-sm py-2.5 px-3 bg-white focus:border-blue-500 focus:ring-blue-500"
                          value={editForm.reportsToId || ''}
                          onChange={(e) => setEditForm({ ...editForm, reportsToId: e.target.value })}
                        >
                          <option value="">-- Select Head --</option>
                          {headsList.filter(h => h.email !== editForm.email).map(h => (
                            <option key={h.id} value={h.id}>{h.name}</option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-slate-700">Assign Departments</label>
                    {editForm.managedDepartments && editForm.managedDepartments.length > 0 && (
                      <button
                        onClick={() => setEditForm({ ...editForm, managedDepartments: [] })}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3 min-h-[32px] p-3 bg-slate-50 rounded-md border border-slate-200">
                    {editForm.managedDepartments?.map((d) => (
                      <span key={d} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-semibold uppercase rounded-md border border-blue-200">
                        {d}
                        <button onClick={() => toggleDepartment(d)} className="hover:text-blue-900 hover:bg-blue-200 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                    {(!editForm.managedDepartments || editForm.managedDepartments.length === 0) && (
                      <span className="text-sm text-slate-400 italic">No departments selected yet. Select from below.</span>
                    )}
                  </div>

                  <div className="relative">
                    <div className="relative mb-3">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                      <input
                        type="text"
                        placeholder="Search departments..."
                        className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-slate-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={deptSearch}
                        onChange={(e) => setDeptSearch(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-1">
                      {filteredDepartments.filter(d => !(editForm.managedDepartments || []).includes(d)).map(d => (
                        <button
                          key={d}
                          onClick={() => toggleDepartment(d)}
                          className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-md hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 text-slate-600 transition-colors flex items-center gap-1 shadow-sm"
                        >
                          <Plus className="w-3 h-3" /> {d}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 bg-slate-100 border border-slate-300 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-[#1C4ED8] hover:bg-blue-700 rounded-md transition-colors shadow-sm"
              >
                Save Mapping
              </button>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};
